import { useEffect, useRef, useState, useCallback } from 'react';
import type { MenuItem } from '@/lib/data';
import type { CartItem } from '../page';
import { VoiceEngine, speechSupported } from '@/lib/voice/speech';
import { interpret, greeting, type CartLite, type ChatTurn } from '@/lib/voice/brain';

interface Props {
  name: string;
  restaurantName: string;
  menu: MenuItem[];
  cart: CartItem[];
  onAddItem: (item: MenuItem, qty: number) => void;
  onSetQty: (item: MenuItem, qty: number) => void;
  onRemoveItem: (itemId: string) => void;
  onClose: () => void;
  onCheckout: () => void;
}

type Phase = 'greeting' | 'listening' | 'thinking' | 'speaking' | 'paused';

const foodEmoji = (ft?: string) => (ft === 'veg' ? '🟢' : ft === 'egg' ? '🟡' : '🔴');

export default function VoiceAssistant({
  name, restaurantName, menu, cart, onAddItem, onSetQty, onRemoveItem, onClose, onCheckout,
}: Props) {
  const engineRef = useRef<VoiceEngine | null>(null);
  const [phase, setPhase] = useState<Phase>('greeting');
  const [aiText, setAiText] = useState('');
  const [userText, setUserText] = useState('');
  const [suggested, setSuggested] = useState<MenuItem[]>([]);
  const [supported] = useState(speechSupported());

  // keep latest cart for the brain without re-subscribing the loop
  const cartRef = useRef(cart);
  cartRef.current = cart;
  const menuById = useRef(new Map(menu.map((m) => [m.id, m])));
  menuById.current = new Map(menu.map((m) => [m.id, m]));

  const cartLite = useCallback((): CartLite[] => cartRef.current.map((c) => ({ name: c.menuItem.name, qty: c.quantity })), []);

  // The conversation so far. Without this the assistant forgets the previous
  // turn, so "make it two" or "haan wahi add karo" have nothing to refer to.
  // Kept in a ref: it must not re-trigger the listen loop when it grows.
  const historyRef = useRef<ChatTurn[]>([]);

  const applyAndSpeak = useCallback(async (transcript: string) => {
    setUserText(transcript);
    setPhase('thinking');
    const result = await interpret(transcript, {
      name,
      restaurant: restaurantName,
      menu,
      cart: cartLite(),
      history: historyRef.current,
    });
    for (const a of result.actions) {
      const item = menuById.current.get(a.itemId);
      if (!item) continue;
      if (a.op === 'add') onAddItem(item, a.qty);
      else if (a.op === 'set') onSetQty(item, a.qty);
      else if (a.op === 'remove') onRemoveItem(item.id);
    }
    // Show a card for every dish the reply talks about — a guest shouldn't have
    // to hold a dish in their head just because it was only spoken.
    setSuggested(result.suggest.map((id) => menuById.current.get(id)).filter((m): m is MenuItem => !!m));
    setAiText(result.reply);
    // Remember this exchange (trimmed — the edge function caps it again).
    historyRef.current = [
      ...historyRef.current,
      { role: 'user', content: transcript },
      { role: 'assistant', content: result.reply },
    ].slice(-8) as ChatTurn[];
    setPhase('speaking');
    await engineRef.current?.speak(result.reply);
    // The guest said they're finished — stop listening rather than awkwardly
    // re-opening the mic.
    if (result.done) { setPhase('paused'); return; }
    listenTurn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, restaurantName, menu, onAddItem, onSetQty, onRemoveItem, cartLite]);

  const listenTurn = useCallback(() => {
    const eng = engineRef.current;
    if (!eng) return;
    setUserText('');
    setPhase('listening');
    eng.listen({
      onPartial: (txt) => setUserText(txt),
      onFinal: (txt) => {
        if (!txt) { setPhase('paused'); return; }
        applyAndSpeak(txt);
      },
      onError: () => setPhase('paused'),
    });
  }, [applyAndSpeak]);

  // boot: greet first, then start listening
  useEffect(() => {
    const eng = new VoiceEngine();
    engineRef.current = eng;
    const hello = greeting(name, restaurantName);
    setAiText(hello);
    (async () => {
      setPhase('greeting');
      await eng.speak(hello);
      if (supported) listenTurn(); else setPhase('paused');
    })();
    return () => { eng.shutUp(); eng.stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tapMic = () => {
    if (phase === 'listening') { engineRef.current?.stop(); setPhase('paused'); }
    else if (phase === 'speaking' || phase === 'greeting') { engineRef.current?.shutUp(); listenTurn(); }
    else listenTurn();
  };

  const subtotal = cart.reduce((s, c) => {
    const base = (c.selectedVariant?.price ?? c.menuItem.price) + c.selectedAddons.reduce((a, x) => a + x.price, 0);
    return s + base * c.quantity;
  }, 0);

  const statusLabel = phase === 'listening' ? 'Listening…'
    : phase === 'thinking' ? 'Thinking…'
    : phase === 'speaking' || phase === 'greeting' ? 'Speaking…'
    : 'Tap the mic to talk';

  return (
    <div className="fixed inset-0 z-[70] bg-gradient-to-b from-primary-950 via-primary-900 to-background-950 text-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-2 shrink-0">
        <div>
          <p className="text-xs uppercase tracking-widest text-white/50">AI Ordering</p>
          <p className="font-semibold">{restaurantName}</p>
        </div>
        <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center cursor-pointer" aria-label="Close">
          <i className="ri-close-line text-xl"></i>
        </button>
      </div>

      {/* Conversation area */}
      <div className="flex-1 overflow-y-auto px-5 pb-3">
        {/* Mic orb */}
        <div className="flex flex-col items-center py-6">
          <button
            onClick={tapMic}
            className={`relative w-28 h-28 rounded-full flex items-center justify-center cursor-pointer transition-transform active:scale-95 ${
              phase === 'listening' ? 'bg-accent-500' : 'bg-white/15'
            }`}
            aria-label="Microphone"
          >
            {phase === 'listening' && <span className="absolute inset-0 rounded-full bg-accent-500/40 animate-ping"></span>}
            {(phase === 'speaking' || phase === 'greeting') && <span className="absolute inset-0 rounded-full bg-white/20 animate-pulse"></span>}
            <i className={`text-4xl ${phase === 'thinking' ? 'ri-loader-4-line animate-spin' : 'ri-mic-fill'}`}></i>
          </button>
          <p className="mt-3 text-sm text-white/70">{statusLabel}</p>
        </div>

        {/* AI + user speech */}
        <div className="max-w-md mx-auto space-y-3">
          {aiText && (
            <div className="bg-white/10 backdrop-blur rounded-2xl rounded-tl-sm px-4 py-3">
              <p className="text-[11px] uppercase tracking-wide text-white/40 mb-1">Assistant</p>
              <p className="text-sm leading-relaxed">{aiText}</p>
            </div>
          )}
          {userText && (
            <div className="bg-accent-500/90 rounded-2xl rounded-tr-sm px-4 py-3 ml-auto max-w-[85%]">
              <p className="text-sm">{userText}</p>
            </div>
          )}
          {!supported && (
            <p className="text-xs text-amber-300 text-center">Voice isn't supported in this browser — you can still tap items below or browse the full menu.</p>
          )}
        </div>

        {/* Dishes referred to in the reply — every dish the assistant talks
            about gets a card, so the guest can SEE what they're hearing and
            decide by looking rather than remembering. */}
        {suggested.length > 0 && (
          <div className="max-w-md mx-auto mt-5">
            <p className="text-xs uppercase tracking-wide text-white/40 mb-2">
              {suggested.length === 1 ? 'This dish' : 'These dishes'}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {suggested.map((m) => {
                const inCart =
                  cart.find((c) => c.menuItem.id === m.id)?.quantity ?? 0;
                return (
                  <button
                    key={m.id}
                    onClick={() => onAddItem(m, 1)}
                    disabled={!m.available}
                    className="relative bg-white/10 hover:bg-white/20 disabled:opacity-40 rounded-xl p-2 text-left cursor-pointer transition-colors"
                  >
                    <div className="h-16 w-full rounded-lg overflow-hidden bg-white/10 mb-1.5 flex items-center justify-center">
                      {m.image ? <img src={m.image} alt={m.name} className="w-full h-full object-cover" /> : <span className="text-2xl">{foodEmoji(m.foodType)}</span>}
                    </div>
                    {/* Veg / non-veg mark — the first thing an Indian guest
                        checks, and not something to convey by voice alone. */}
                    <span
                      className={`absolute top-3 left-3 w-3 h-3 rounded-sm border-2 flex items-center justify-center ${
                        m.foodType === 'veg' ? 'border-green-400' : 'border-red-400'
                      }`}
                      aria-label={m.foodType === 'veg' ? 'Veg' : 'Non-veg'}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          m.foodType === 'veg' ? 'bg-green-400' : 'bg-red-400'
                        }`}
                      />
                    </span>
                    {inCart > 0 && (
                      <span className="absolute top-2 right-2 bg-accent-500 text-[10px] font-bold rounded-full px-1.5 py-0.5">
                        {inCart}
                      </span>
                    )}
                    <p className="text-[11px] font-medium leading-tight line-clamp-2">{m.name}</p>
                    <p className="text-[11px] text-white/60">
                      {m.available ? `₹${m.price}` : 'Sold out'}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Live order */}
        <div className="max-w-md mx-auto mt-6">
          <p className="text-xs uppercase tracking-wide text-white/40 mb-2">Your order</p>
          {cart.length === 0 ? (
            <p className="text-sm text-white/50">Nothing yet — tell me what you'd like!</p>
          ) : (
            <div className="space-y-2">
              {cart.map((c) => (
                <div key={c.menuItem.id + (c.selectedVariant?.name ?? '')} className="flex items-center gap-3 bg-white/10 rounded-xl p-2.5">
                  <div className="h-12 w-12 rounded-lg overflow-hidden bg-white/10 shrink-0 flex items-center justify-center">
                    {c.menuItem.image ? <img src={c.menuItem.image} alt={c.menuItem.name} className="w-full h-full object-cover" /> : <span className="text-xl">{foodEmoji(c.menuItem.foodType)}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.menuItem.name}</p>
                    <p className="text-xs text-white/60">₹{(c.selectedVariant?.price ?? c.menuItem.price)} each</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => onSetQty(c.menuItem, c.quantity - 1)} className="w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center cursor-pointer"><i className="ri-subtract-line"></i></button>
                    <span className="w-5 text-center text-sm font-semibold">{c.quantity}</span>
                    <button onClick={() => onSetQty(c.menuItem, c.quantity + 1)} className="w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center cursor-pointer"><i className="ri-add-line"></i></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="shrink-0 px-5 py-4 bg-black/20 backdrop-blur border-t border-white/10 flex items-center gap-3">
        <button onClick={onClose} className="px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-medium cursor-pointer whitespace-nowrap">
          Browse menu
        </button>
        <button
          onClick={onCheckout}
          disabled={cart.length === 0}
          className="flex-1 px-4 py-3 rounded-xl bg-accent-500 hover:bg-accent-600 text-sm font-semibold cursor-pointer whitespace-nowrap disabled:opacity-40"
        >
          Place Order · ₹{subtotal.toFixed(0)}
        </button>
      </div>
    </div>
  );
}
